/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import * as Apollo from "@apollo/client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { PaginationWrapperProps, UseGeneratedQueryOptions } from "@/types/pagination";
import React, { JSX, ReactNode, useImperativeHandle } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Oval } from "react-loader-spinner";
import { usePaginatedQuery } from "@/hooks/paginatedQuery";
import { useTranslation } from "react-i18next";
import { FramerButton } from "./common";

/**
 * Props for pagination layout support components.
 */
type Props = {
  children?: React.ReactNode | React.ReactNode[];
};

/**
 * Component to support pagination layout. Use this with `PaginationContentScrollable` nested inside
 * when additional non-scrollable components are included within the Pagination component
 * (e.g., filter components).
 *
 * @param {React.ReactNode | React.ReactNode[]} children - React node to be wrapped in.
 * @returns {JSX.Element} The wrapper component for pagination content.
 *
 * @example
 * ```javascript
 * <PaginationContentWrapper>
 *   <FilterComponent />
 *   <PaginationContentScrollable>
 *     <TableComponent />
 *   </PaginationContentScrollable>
 * </PaginationContentWrapper>
 * ```
 */
function PaginationContentWrapper({ children }: Props): JSX.Element {
  return (
    <div className="flex flex-col flex-grow space-y-4">
      {children}
    </div>
  );
}

/**
 * Component to support pagination layout, where content within this component will be scrollable.
 *
 * @param {React.ReactNode | React.ReactNode[]} children - React node to be wrapped in.
 * @param {HTMLDivElement} ref - A ref to the scrollable div element.
 * @returns {JSX.Element} The scrollable content wrapper.
 *
 * @example
 * ```javascript
 * <PaginationContentScrollable ref={scrollableRef}>
 *   <TableComponent />
 * </PaginationContentScrollable>
 * ```
 */
function PaginationContentScrollable({
  children,
  ref
}: Props & { ref?: React.Ref<HTMLDivElement> }): JSX.Element {
  return (
    <div ref={ref} className="flex flex-col flex-grow overflow-x-hidden overflow-y-auto space-y-4 h-[300px]">
      {children}
    </div>
  );
}

/**
 * This component holds all UI for Pagination. It calls the pagination hook and passes the `dataSlice` to the child component.
 *
 * @template TEdge - The type of each item in the data slice.
 * @template TData - The type of the query data.
 * @template TVariables - The type of the query variables.
 * @param {PaginationWrapperProps<TEdge, TData, TVariables>} props - Props including query hook, pagination name, and more.
 * @param {React.Ref} ref - Ref to trigger `handleFilterSubmit` from the parent component.
 * @returns {JSX.Element} The Pagination component.
 *
 * @example
 * ```javascript
 * const paginationRef = useRef();
 *
 * <Pagination
 *   ref={paginationRef}
 *   props={{
 *     queryHook: useGetItemsQuery,
 *     paginationName: "itemsPagination",
 *   }}
 * >
 *   {(dataSlice, loading, displayAmount, subscribeToMore) => (
 *     <TableComponent data={dataSlice} loading={loading} />
 *   )}
 * </Pagination>
 * ```
 */
function Pagination<TEdge, TData, TVariables extends Apollo.OperationVariables>({
  props,
  children,
  ref
}: PaginationWrapperProps<TEdge, TData, TVariables> & {
  ref?: React.Ref<{
    triggerHandleFilterSubmit: (_newVariables: Partial<TVariables>) => void,
    getData?: () => TEdge[],
    getLoading?: () => boolean,
    getSubscribeToMore?: any; // SubscribeToMoreFn<TData, TVariables>
  }>
}): JSX.Element {

  const { queryHook, paginationName, reversePagination, className } = props;
  const options: UseGeneratedQueryOptions<TData, TVariables> = { queryHook };

  const {
    loading,
    dataSlice,
    handleNextPage,
    subscribeToMore,
    handlePreviousPage,
    handleDisplayAmount,
    handleFilterSubmit,
    loadingMoreData,
    displayAmount,
    showingFrom,
    showingTo,
    rowsFetched,
  } = usePaginatedQuery<TEdge, TData, TVariables>(options, paginationName, reversePagination);

  // Hooks
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    triggerHandleFilterSubmit(newVariables: Partial<TVariables>) {
      handleFilterSubmit(newVariables);
    },
    getData() {
      return dataSlice; // Ensure that this returns the current slice of data
    },
    getLoading() {
      return loading || loadingMoreData;
    },
    getSubscribeToMore: subscribeToMore,
  }));

  return (
    <div className="flex flex-col flex-grow w-full">
      {/* Table goes here */}
      <div className="flex flex-col flex-grow overflow-y-hidden">
        {children(dataSlice, loading || loadingMoreData, displayAmount, subscribeToMore) as ReactNode}
      </div>

      {/* Pagination buttons */}
      <div className={`${className} select-none flex items-center pt-2`}>
        <FramerButton
          onClick={handlePreviousPage}
          variant="outline"
          size="icon"
          className="bg-white dark:bg-mfdarklight border-[1px] p-1 mr-2"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </FramerButton>

        <FramerButton
          disabled={loading || loadingMoreData}
          onClick={handleNextPage}
          variant="outline"
          size="icon"
          className={!loadingMoreData ? "bg-white dark:bg-mfdarklight p-1 mr-2" : "bg-white dark:bg-mfdarklight"}
        >
          {
            loadingMoreData ?
              <Oval
                height={24}
                width={24}
                color="#EE5300"
                wrapperStyle={{}}
                wrapperClass=""
                visible
                ariaLabel='oval-loading'
                secondaryColor="#EE5300"
                strokeWidth={8}
                strokeWidthSecondary={8}
              />:
              <ChevronRightIcon className="w-6 h-6" />
          }
        </FramerButton>

        <div className="flex items-center space-x-2">
          {/* Display Amount Toggle */}

          <Select
            defaultValue={displayAmount?.toString()}
            onValueChange={(value) => {
              handleDisplayAmount(parseInt(value ?? ""));
            }}
          >
            <SelectTrigger
              className="select-none cursor-pointer dark:bg-mfdarklight dark:placeholder:text-gray-200 max-w-[70px] p-1 w-[50px]"
            >
              <SelectValue placeholder="Display Amount" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-mfdarklight">
              {[5, 10, 25, 50].map((displayAmount) => (
                <SelectItem key={displayAmount} value={displayAmount.toString()}>
                  {displayAmount}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="hidden md:flex flex-col">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {// Assets fetched: **max amount**
                `Fetched ${rowsFetched} rows`
              }
            </p>

            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {// Showing assets: **start** to **end**
                `Showing: ${showingFrom} ${t("common:text.to")} ${showingTo}`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export { PaginationContentScrollable, PaginationContentWrapper, Pagination };
